import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Brain,
  CheckCircle2,
  Database,
  HeartPulse,
  Info,
  Search,
  ShieldCheck,
  Smartphone,
  Zap,
  HelpCircle,
  Loader2,
  Cpu,
  HardDrive,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import type { RemedyDetail } from './LandingPagePro';

const DEFAULT_SAMPLE_REMEDIES: Record<string, RemedyDetail> = {
  Arnica: {
    name: 'Arnica Montana',
    short_name: 'arn.',
    full_name: 'ARNICA MONTANA',
    family: 'Asteraceae (Compositae)',
    keynotes: [
      'Sensation of sore, bruised feeling all over the body.',
      'Physical overexertion, muscle stiffness, or temporary discomfort.',
      'Soreness upon touch or physical movement.',
    ],
    modalities: {
      agg: 'By touch, movement, physical exertion.',
      amel: 'By absolute rest, lying down quietly.',
    },
    matchScore: 98,
  },
  Belladonna: {
    name: 'Atropa Belladonna',
    short_name: 'bell.',
    full_name: 'ATROPA BELLADONNA',
    family: 'Solanaceae',
    keynotes: [
      'Sudden sensation of warmth and redness.',
      'Hypersensitivity to bright light, noise, or sudden movement.',
      'Throbbing sensations during acute temporary states.',
    ],
    modalities: {
      agg: 'Afternoon hours, bright light, sudden touch.',
      amel: 'Resting in a quiet, darkened room.',
    },
    matchScore: 91,
  },
  Bryonia: {
    name: 'Bryonia Alba',
    short_name: 'bry.',
    full_name: 'BRYONIA ALBA',
    family: 'Cucurbitaceae',
    keynotes: [
      'Discomfort worsened by the slightest physical movement.',
      'Sensory dryness of mucous membranes with thirst for cold water.',
      'Relief felt from firm, steady pressure.',
    ],
    modalities: {
      agg: 'By any movement, heat, early morning.',
      amel: 'By absolute rest, pressure on affected area.',
    },
    matchScore: 96,
  },
  'Nux Vomica': {
    name: 'Nux Vomica',
    short_name: 'nux-v.',
    full_name: 'NUX VOMICA',
    family: 'Loganiaceae',
    keynotes: [
      'Hypersensitivity to environmental stimuli and stress.',
      'Tension following overwork, coffee, or irregular schedules.',
      'Chilly sensation upon sudden exposure to draft or cold.',
    ],
    modalities: {
      agg: 'Morning upon waking, cold air, stimulant intake.',
      amel: 'Resting quietly in bed, warmth.',
    },
    matchScore: 89,
  },
};

export function LandingPageDTC() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('Arnica');
  const [remediesList, setRemediesList] = useState<RemedyDetail[]>(Object.values(DEFAULT_SAMPLE_REMEDIES));
  const [selectedRemedy, setSelectedRemedy] = useState<RemedyDetail>(DEFAULT_SAMPLE_REMEDIES.Arnica);
  const [suggestions, setSuggestions] = useState<RemedyDetail[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch remedy catalog from backend
  useEffect(() => {
    let isMounted = true;
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const response = await apiClient.get('/remedies/catalog');
        if (isMounted && response.data?.remedies && Array.isArray(response.data.remedies) && response.data.remedies.length > 0) {
          setRemediesList(response.data.remedies);
          const arnicaInDb = response.data.remedies.find((r: RemedyDetail) =>
            r.name.toLowerCase().includes('arnica') || (r.short_name && r.short_name.toLowerCase().includes('arn'))
          );
          if (arnicaInDb) {
            setSelectedRemedy(arnicaInDb);
          }
        }
      } catch {
        // Fallback active
      } finally {
        if (isMounted) {
          setLoadingCatalog(false);
        }
      }
    };

    fetchCatalog();
    return () => {
      isMounted = false;
    };
  }, []);

  // Update suggestions
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }

    const query = searchTerm.trim().toLowerCase();
    const filtered = remediesList.filter((r) =>
      r.name.toLowerCase().includes(query) ||
      (r.full_name && r.full_name.toLowerCase().includes(query)) ||
      (r.short_name && r.short_name.toLowerCase().includes(query))
    );

    setSuggestions(filtered.slice(0, 6));
  }, [searchTerm, remediesList]);

  // Click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectRemedy = (remedy: RemedyDetail) => {
    setSelectedRemedy(remedy);
    setSearchTerm(remedy.name);
    setIsDropdownOpen(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSelectRemedy(suggestions[0]);
    } else {
      const query = searchTerm.trim().toLowerCase();
      const match = remediesList.find((r) =>
        r.name.toLowerCase().includes(query) ||
        (r.full_name && r.full_name.toLowerCase().includes(query)) ||
        (r.short_name && r.short_name.toLowerCase().includes(query))
      );
      if (match) {
        handleSelectRemedy(match);
      }
    }
  };

  const handleSelectPill = (remedyName: string) => {
    const found = remediesList.find((r) =>
      r.name.toLowerCase().includes(remedyName.toLowerCase()) ||
      (r.full_name && r.full_name.toLowerCase().includes(remedyName.toLowerCase())) ||
      (r.short_name && r.short_name.toLowerCase().includes(remedyName.toLowerCase()))
    ) || DEFAULT_SAMPLE_REMEDIES[remedyName];

    if (found) {
      handleSelectRemedy(found);
    }
  };

  const handleGetApp = () => {
    alert('SimilDoc.app for Android is coming soon to the Google Play Store.');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-blue-500 selection:text-white font-sans relative overflow-x-hidden">
      {/* Soft Glow Background Spotlights in Blue / Sky / Indigo Tones */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[550px] bg-gradient-to-tr from-blue-200/30 via-sky-100/40 to-indigo-200/30 blur-[140px] rounded-full pointer-events-none -z-0 animate-pulse-slow" />
      <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] bg-blue-100/40 blur-[160px] rounded-full pointer-events-none -z-0" />
      <div className="absolute top-[75%] left-[-10%] w-[600px] h-[600px] bg-sky-100/40 blur-[160px] rounded-full pointer-events-none -z-0" />

      {/* 1. TOP LEGAL DISCLOSURE STRIP */}
      <aside aria-label="Mandatory legal information" className="bg-amber-500/10 border-b border-amber-500/20 text-amber-900 text-xs py-2.5 px-4 text-center font-medium">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
          <Info className="w-4 h-4 text-amber-700 shrink-0" />
          <span>
            <strong>Mandatory Legal Notice:</strong> This application provides informative recommendations based on traditional homeopathic principles for educational and wellness purposes only. It does not constitute a medical consultation or diagnostic service.
          </span>
        </div>
      </aside>

      {/* 2. FLOATING NAVBAR */}
      <header className="sticky top-4 z-50 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
        <div className="glass-panel-light rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg shadow-slate-200/50 border border-slate-200/80">
          <button
            type="button"
            className="flex items-center gap-3 cursor-pointer text-left bg-transparent border-0 p-0"
            onClick={() => navigate('/')}
          >
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20">
              <Activity className="w-5 h-5 font-bold" />
            </div>
            <div>
              <span className="text-lg font-extrabold tracking-tight text-slate-900">
                SimilDOC <span className="text-blue-600 font-semibold">Care</span>
              </span>
              <span className="hidden sm:inline-block ml-2 px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                Terminal Processing & Storage
              </span>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#how-it-works" className="hover:text-blue-600 transition-colors">
              How It Works
            </a>
            <a href="#privacy-first" className="hover:text-blue-600 transition-colors">
              Terminal Architecture
            </a>
            <a href="#demo" className="hover:text-blue-600 transition-colors">
              Remedy Explorer
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleGetApp}
              className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 rounded-xl shadow-md shadow-blue-600/20 hover:shadow-blue-600/35 transition-all flex items-center gap-1.5"
            >
              Get SimilDoc.app on Android Store <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* 3. HERO SECTION */}
      <section className="relative pt-14 pb-20 md:pt-24 md:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* On-Device & Zero Retention Security Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-light border border-blue-200 text-blue-800 text-xs sm:text-sm font-medium mb-8 glow-pill-light">
          <Smartphone className="w-4 h-4 text-blue-600 animate-pulse" />
          <span>100% Terminal Storage & Processing &bull; Zero Data Retention in the Cloud</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1] max-w-5xl mx-auto">
          Personal Homeopathic{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600">
            Guidance
          </span>
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-normal">
          An educational and personal wellness tool. The complete homeopathic database and your records are stored and processed locally on your smartphone terminal, using lightweight anonymous auxiliary vectorization with strict zero cloud retention.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            onClick={handleGetApp}
            className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 rounded-2xl shadow-xl shadow-blue-600/25 hover:shadow-blue-600/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
          >
            Get SimilDoc.app on Android Store <ArrowRight className="w-5 h-5" />
          </button>
          <a
            href="#demo"
            className="w-full sm:w-auto px-8 py-4 text-base font-semibold text-slate-700 glass-card-light hover:bg-white rounded-2xl border border-slate-200/90 hover:border-blue-500/40 transition-all flex items-center justify-center gap-2"
          >
            <Search className="w-5 h-5 text-blue-600" /> Explore Remedy Database
          </a>
        </div>

        {/* Trust Bullets */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-slate-500 font-medium">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-blue-600" /> Terminal-Based Processing
          </div>
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-indigo-600" /> Local Smartphone Storage
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-sky-600" /> Repertory with 650+ Remedies
          </div>
          <div className="flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-blue-700" /> Wellness & Educational Scope
          </div>
        </div>
      </section>

      {/* 4. MANDATORY LEGAL DISCLAIMER CALLOUT */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-16">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-blue-500/5 border border-amber-200/80 shadow-md flex flex-col sm:flex-row items-start gap-5">
          <div className="p-3 bg-amber-100 rounded-2xl text-amber-800 shrink-0">
            <HelpCircle className="w-6 h-6" />
          </div>
          <div className="space-y-2 text-left">
            <h2 className="text-base font-bold text-amber-950">
              Legal Notice & Healthcare Scope Demarcation
            </h2>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              <strong>&ldquo;This application provides informative recommendations based on traditional homeopathic principles for educational and lifestyle purposes only. It does not constitute a medical consultation, does not diagnose medical conditions, and does not replace the professional judgment, diagnosis, or treatment prescribed by a licensed healthcare provider.&rdquo;</strong>
            </p>
          </div>
        </div>
      </section>

      {/* 5. TERMINAL ARCHITECTURE & STORAGE SECTION */}
      <section id="privacy-first" className="py-16 md:py-24 bg-white border-y border-slate-200/80 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold text-blue-700 tracking-widest uppercase bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
              Terminal-Centric Architecture
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-4">
              Local Storage and Processing on Your Smartphone
            </h2>
            <p className="mt-3 text-slate-600 text-base sm:text-lg">
              Your wellness data and the classical repertory database live on your device. Computation runs locally on your terminal, with zero persistent data stored in the cloud.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1: 100% Terminal Data Storage */}
            <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-slate-50/50 hover:bg-white transition-all shadow-sm">
              <div className="p-3.5 w-fit rounded-2xl bg-blue-50 text-blue-600 mb-5 border border-blue-200">
                <HardDrive className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">1. Local Storage on Terminal</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                All symptom histories, notes, and the repertory database are stored in your device&apos;s internal storage. Your personal information never resides on remote servers.
              </p>
              <div className="mt-4 text-xs font-semibold text-blue-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> 100% Local data sovereignty
              </div>
            </div>

            {/* Card 2: On-Device Terminal Processing */}
            <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-slate-50/50 hover:bg-white transition-all shadow-sm">
              <div className="p-3.5 w-fit rounded-2xl bg-indigo-50 text-indigo-600 mb-5 border border-indigo-200">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">2. On-Terminal Processing</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Repertorization calculations and rubric evaluations are performed natively on your mobile phone chip, ensuring instant responsiveness and privacy.
              </p>
              <div className="mt-4 text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Native device execution
              </div>
            </div>

            {/* Card 3: Ephemeral Auxiliary Vectorization */}
            <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-slate-50/50 hover:bg-white transition-all shadow-sm">
              <div className="p-3.5 w-fit rounded-2xl bg-sky-50 text-sky-600 mb-5 border border-sky-200">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">3. Ephemeral Auxiliary Parsing</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Freeform descriptions are de-identified locally before a lightweight auxiliary vectorization step in the cloud, operating with strict Zero Data Retention (ZDR).
              </p>
              <div className="mt-4 text-xs font-semibold text-sky-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Zero data retention in the cloud
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. INTERACTIVE REPERTORY EXPLORER DEMO */}
      <section id="demo" className="py-16 md:py-24 bg-slate-50 border-b border-slate-200/80 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold text-blue-700 tracking-widest uppercase bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
              Educational Repertory
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-4">
              Explore 650+ remedies from classical Materia Medica
            </h2>
            <p className="mt-3 text-slate-600 text-base sm:text-lg">
              Inspect traditional keynotes, aggravation, and amelioration modalities stored in the local catalog.
            </p>
          </div>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto mb-10 relative" ref={dropdownRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative flex items-center">
                <Search className="absolute left-4 w-5 h-5 text-blue-600" />
                <input
                  type="text"
                  value={searchTerm}
                  onFocus={() => setIsDropdownOpen(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  placeholder="Search the repertory (e.g. Arnica, Belladonna, Bryonia)..."
                  className="w-full pl-12 pr-28 py-4 rounded-2xl bg-white text-slate-900 placeholder-slate-400 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-base transition-all shadow-sm"
                />
                <button
                  type="submit"
                  className="absolute right-2.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl text-sm transition-colors shadow-md shadow-blue-600/20 flex items-center gap-1"
                >
                  {loadingCatalog && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Search
                </button>
              </div>
            </form>

            {/* Dropdown */}
            {isDropdownOpen && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl z-30 max-h-72 overflow-y-auto divide-y divide-slate-100">
                {suggestions.map((remedy) => (
                  <div
                    key={remedy.short_name || remedy.name}
                    onClick={() => handleSelectRemedy(remedy)}
                    className="px-4 py-3 hover:bg-blue-50/70 cursor-pointer flex items-center justify-between transition-colors text-left"
                  >
                    <div>
                      <span className="font-semibold text-slate-900 text-sm block">
                        {remedy.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {remedy.family} {remedy.short_name ? `• ${remedy.short_name}` : ''}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-blue-700 bg-blue-100/60 px-2 py-0.5 rounded-full">
                      Match: {remedy.matchScore}%
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Pills */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">Frequently checked:</span>
              {['Arnica', 'Belladonna', 'Bryonia', 'Nux Vomica'].map((pill) => {
                const isActive = selectedRemedy.name.toLowerCase().includes(pill.toLowerCase()) ||
                  (selectedRemedy.short_name && selectedRemedy.short_name.toLowerCase().includes(pill.toLowerCase()));
                return (
                  <button
                    key={pill}
                    type="button"
                    onClick={() => handleSelectPill(pill)}
                    className={`px-3 py-1 rounded-full border transition-all ${
                      isActive
                        ? 'bg-blue-50 border-blue-500 text-blue-700 font-semibold shadow-xs'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    {pill}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Remedy Card */}
          <div className="max-w-4xl mx-auto glass-panel-light rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl relative overflow-hidden bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
              <div>
                <span className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
                  Informative Remedy Profile
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                  {selectedRemedy.name}
                </h3>
                <span className="text-sm text-slate-500">
                  {selectedRemedy.family} {selectedRemedy.short_name ? `(${selectedRemedy.short_name})` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-3.5 py-1.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 font-bold text-sm flex items-center gap-1.5">
                  <Zap className="w-4 h-4 fill-blue-600 text-blue-600" /> Match: {selectedRemedy.matchScore}%
                </div>
                <button
                  type="button"
                  onClick={handleGetApp}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl shadow-sm transition-all"
                >
                  Get App
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 pt-6">
              <div>
                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-blue-600" /> Traditional Keynote Symptoms
                </h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  {selectedRemedy.keynotes && selectedRemedy.keynotes.length > 0 ? (
                    selectedRemedy.keynotes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>{item}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-slate-400 italic">No specific keynotes listed.</li>
                  )}
                </ul>
              </div>

              <div className="space-y-4">
                <div className="bg-red-50/60 rounded-2xl p-4 border border-red-100">
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wider block mb-1">
                    Aggravation Modality (Worse)
                  </span>
                  <p className="text-sm text-slate-700">
                    {selectedRemedy.modalities?.agg || 'Factors defined in classical repertory.'}
                  </p>
                </div>
                <div className="bg-sky-50/60 rounded-2xl p-4 border border-sky-100">
                  <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-1">
                    Amelioration Modality (Better)
                  </span>
                  <p className="text-sm text-slate-700">
                    {selectedRemedy.modalities?.amel || 'Relief factors defined in classical repertory.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. HOW IT WORKS (Terminal-centric workflow) */}
      <section id="how-it-works" className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold text-blue-700 tracking-widest uppercase bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
            Terminal-Centric Workflow
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-4">
            Private, Local Symptom Guidance
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            How your sensations are recorded locally and analyzed on your terminal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-white relative">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-blue-600/20">
              1
            </div>
            <h3 className="text-xl font-bold text-slate-900">1. Local Terminal Input</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Describe your physical sensations in everyday words. All records are saved exclusively to your smartphone storage.
            </p>
          </div>

          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-white relative">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-indigo-600/20">
              2
            </div>
            <h3 className="text-xl font-bold text-slate-900">2. Terminal Processing</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Repertorization algorithms compute matches locally against classical rubrics, using lightweight cloud parsing with zero data retention.
            </p>
          </div>

          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 bg-white relative">
            <div className="w-10 h-10 rounded-2xl bg-sky-600 text-white font-bold flex items-center justify-center mb-6 shadow-md shadow-sky-600/20">
              3
            </div>
            <h3 className="text-xl font-bold text-slate-900">3. Educational Profile</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              View candidate homeopathic remedies and their keynote characteristics instantly on your mobile screen.
            </p>
          </div>
        </div>
      </section>

      {/* 8. BOTTOM CTA */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <div className="glass-card-light rounded-3xl p-10 sm:p-16 border border-slate-200 relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-blue-50/40 shadow-xl">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight max-w-3xl mx-auto">
            Start your personal natural wellness exploration
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
            Discover classical remedies with terminal processing, local storage, and zero cloud data retention.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              type="button"
              onClick={handleGetApp}
              className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 rounded-2xl shadow-xl shadow-blue-600/25 hover:shadow-blue-600/40 transition-all flex items-center justify-center gap-2"
            >
              Get SimilDoc.app on Android Store <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* 9. FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-12 text-slate-500 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 text-left">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-900 font-bold text-lg">
                <HeartPulse className="w-5 h-5 text-blue-600" /> SimilDOC Care
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Personal homeopathic guidance and repertorization assistant based on traditional principles, terminal processing, and local smartphone storage.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Explore
              </h4>
              <ul className="space-y-2 text-xs">
                <li>
                  <a href="#how-it-works" className="hover:text-blue-600 transition-colors">
                    How It Works
                  </a>
                </li>
                <li>
                  <a href="#demo" className="hover:text-blue-600 transition-colors">
                    Remedy Database
                  </a>
                </li>
                <li>
                  <a href="#privacy-first" className="hover:text-blue-600 transition-colors">
                    Terminal Architecture
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Legal
              </h4>
              <ul className="space-y-2 text-xs">
                <li>
                  <span className="hover:text-slate-700">Privacy Policy</span>
                </li>
                <li>
                  <span className="hover:text-slate-700">Terms of Service (Wellness)</span>
                </li>
                <li>
                  <span className="hover:text-slate-700">Non-Medical Scope Notice</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Mandatory Full Medical Disclaimer Banner */}
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-950 mb-8 leading-relaxed text-left">
            <span className="font-bold">Medical Disclaimer:</span> SimilDOC Care is an automated educational and informative application on classical homeopathy. It does not perform medical diagnoses, prescribe pharmaceutical treatments, or provide healthcare services. If you have persistent symptoms or medical concerns, always consult a licensed physician or healthcare professional.
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-slate-400 pt-4 border-t border-slate-100">
            <div>© {new Date().getFullYear()} SimilDOC Care. All rights reserved.</div>
            <div className="mt-2 sm:mt-0 flex gap-4">
              <span>Terminal Processing</span>
              <span>•</span>
              <span>Local Smartphone Storage</span>
              <span>•</span>
              <span>Zero Data Retention in the Cloud</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
